/**
 * Do-not-call list. Bundled via import from app.js.
 * Props: api (API.json), toast, user, timezone.
 */
const { useState, useEffect, useCallback } = React;

const TILES = [
  ['', 'All numbers'],
  ['internal_company_dnc', 'Your company list'],
  ['official_national_dnc', 'National registry file'],
  ['state_dnc', 'State registry file'],
  ['sms_opt_out', 'Opted out by text'],
  ['verbal_opt_out', 'Asked not to be called']
];

const IMPORT_SOURCES = [
  ['internal_company_dnc', 'Your company list'],
  ['official_national_dnc', 'National registry file'],
  ['state_dnc', 'State registry file'],
  ['test_sample', 'Test sample (not a legal registry)']
];

const PAGE = 25;

function errorMessage(err, fallback) {
  const data = err && err.data;
  return (data && (data.detail || data.message)) || (err && err.message) || fallback;
}

function formatAdded(iso, timeZone) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || undefined,
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
  }
}

function apiBase() {
  if (typeof window !== 'undefined' && window.API_BASE) return String(window.API_BASE).replace(/\/$/, '');
  return '';
}

function authHeaders() {
  const headers = {};
  const token = typeof window !== 'undefined' ? window.localStorage.getItem('authToken') : null;
  if (token) headers.Authorization = 'Bearer ' + token;
  return headers;
}

export function DncManager({ api, toast, user, timezone }) {
  const [counts, setCounts] = useState({});
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [source, setSource] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [confirmJob, setConfirmJob] = useState(null);
  const [importSource, setImportSource] = useState('internal_company_dnc');
  const [importFile, setImportFile] = useState(null);
  const zone = timezone || 'America/Los_Angeles';
  const canManage = !!(user && (user.role === 'tenant_admin' || user.role === 'supervisor' || user.role === 'super_admin' || user.role === 'superadmin'));

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (source) params.set('source', source);
      params.set('limit', String(PAGE));
      params.set('offset', String(offset));
      const data = await api('GET', '/api/dnc?' + params.toString());
      setCounts(data.counts || {});
      setItems(data.items || []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      setError(errorMessage(err, 'Could not load the do-not-call list.'));
    } finally {
      setLoading(false);
    }
  }, [api, search, source, offset]);

  useEffect(() => { load(); }, [load]);

  async function addNumber(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const data = await api('POST', '/api/dnc', { phone, reason });
      setPhone('');
      setReason('');
      toast(data && data.unchanged ? 'That number is already on a registry file.' : 'Added to your do-not-call list.');
      setOffset(0);
      await load();
    } catch (err) {
      toast(errorMessage(err, 'Could not add that number.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function removeRow(item) {
    setBusy(true);
    try {
      await api('DELETE', '/api/dnc/' + item.id);
      setConfirmId(null);
      toast('Removed from the list.');
      await load();
    } catch (err) {
      toast(errorMessage(err, 'Could not remove that number.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function removeImport(jobId) {
    setBusy(true);
    try {
      await api('DELETE', '/api/dnc/imports/' + jobId);
      setConfirmJob(null);
      toast('Removed that file import.');
      await load();
    } catch (err) {
      toast(errorMessage(err, 'Could not remove that file.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function importCsv(event) {
    event.preventDefault();
    if (!importFile) {
      toast('Choose a CSV or TXT file.', 'error');
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', importFile);
      form.append('source_type', importSource || 'internal_company_dnc');
      const res = await fetch(apiBase() + '/api/dnc/import', {
        method: 'POST',
        headers: authHeaders(),
        body: form,
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw Object.assign(new Error(data.detail || data.error || 'Import failed'), { data });
      setImportFile(null);
      toast('Imported ' + (data.imported || 0) + ' numbers.');
      await load();
    } catch (err) {
      toast(errorMessage(err, 'Import failed.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function exportCsv() {
    setBusy(true);
    try {
      const res = await fetch(apiBase() + '/api/dnc/export.csv', {
        headers: authHeaders(),
        credentials: 'include'
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw Object.assign(new Error(data.detail || data.error || 'Export failed'), { data });
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'dnc.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast(errorMessage(err, 'Could not export the list.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  const allCount = Object.keys(counts).reduce((sum, key) => sum + Number(counts[key] || 0), 0);
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE, total);

  return (
    <div className="cmp">
      <div className="cmp-header">
        <div>
          <h1 className="cmp-title">Do not call</h1>
          <p className="cmp-lead">We never call or text these numbers.</p>
        </div>
        <button type="button" className="cmp-btn" disabled={busy} onClick={exportCsv}>Export CSV</button>
      </div>
      <p className="cmp-note">
        A National DNC registry subscription (SAN) is needed to download the federal list. Upload that file here.
        We do not scrub the national registry for you.
      </p>
      <div className="cmp-tiles">
        {TILES.map(([key, label]) => (
          <button
            key={key || 'all'}
            type="button"
            className={'cmp-tile' + (source === key ? ' cmp-tile--active' : '')}
            onClick={() => { setSource(key); setOffset(0); }}
          >
            <strong>{key ? Number(counts[key] || 0) : allCount}</strong>
            <span>{label}</span>
          </button>
        ))}
        {Number(counts.test_sample || 0) > 0 ? (
          <button
            type="button"
            className={'cmp-tile' + (source === 'test_sample' ? ' cmp-tile--active' : '')}
            onClick={() => { setSource('test_sample'); setOffset(0); }}
          >
            <strong>{Number(counts.test_sample || 0)}</strong>
            <span>Test sample</span>
          </button>
        ) : null}
      </div>
      <form className="cmp-card cmp-stack" onSubmit={addNumber}>
        <h2>Add a number</h2>
        <div className="cmp-row">
          <label className="cmp-field cmp-grow">
            Phone
            <input className="cmp-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(415) 555-0100" required />
          </label>
          <label className="cmp-field cmp-grow">
            Reason
            <input className="cmp-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Asked us to stop" />
          </label>
          <button type="submit" className="cmp-btn cmp-btn--primary" disabled={busy}>Add number</button>
        </div>
      </form>
      <form className="cmp-toolbar" onSubmit={(e) => { e.preventDefault(); setOffset(0); setSearch(searchInput.trim()); }}>
        <input
          className="cmp-input cmp-grow"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search number or reason"
          aria-label="Search do-not-call list"
        />
        <button type="submit" className="cmp-btn">Search</button>
      </form>
      {error ? <div className="cmp-error">{error}</div> : null}
      <div className="cmp-table-wrap">
        {loading ? <p className="cmp-meta">Loading…</p> : null}
        {!loading && !items.length ? <div className="cmp-empty">No numbers on this list yet.</div> : null}
        {items.length ? (
          <table className="cmp-table">
            <thead>
              <tr>
                <th>Number</th>
                <th>Added</th>
                <th>Source</th>
                <th>Reason</th>
                <th>Added by</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="cmp-phone">{item.phone}</td>
                  <td>{formatAdded(item.added_at, zone)}</td>
                  <td>{item.source_label}{item.file_label ? <div className="cmp-meta">{item.file_label}</div> : null}</td>
                  <td>{item.reason || '—'}</td>
                  <td>{item.added_by_name || (item.import_job_id ? 'File import' : '—')}</td>
                  <td>
                    {confirmId === item.id ? (
                      <div className="cmp-actions">
                        <span>Remove this number?</span>
                        <button type="button" className="cmp-btn cmp-btn--danger" disabled={busy} onClick={() => removeRow(item)}>Confirm</button>
                        <button type="button" className="cmp-btn" disabled={busy} onClick={() => setConfirmId(null)}>Cancel</button>
                      </div>
                    ) : null}
                    {confirmJob === item.import_job_id && item.registry ? (
                      <div className="cmp-actions">
                        <span>Remove every number from this file?</span>
                        <button type="button" className="cmp-btn cmp-btn--danger" disabled={busy} onClick={() => removeImport(item.import_job_id)}>Confirm</button>
                        <button type="button" className="cmp-btn" disabled={busy} onClick={() => setConfirmJob(null)}>Cancel</button>
                      </div>
                    ) : null}
                    {item.removable && canManage && confirmId !== item.id ? (
                      <button type="button" className="cmp-btn" disabled={busy} onClick={() => setConfirmId(item.id)}>Remove</button>
                    ) : null}
                    {item.registry && canManage && item.import_job_id && confirmJob !== item.import_job_id ? (
                      <button type="button" className="cmp-btn" disabled={busy} onClick={() => setConfirmJob(item.import_job_id)}>Remove file</button>
                    ) : null}
                    {item.registry && !item.import_job_id ? (
                      <span className="cmp-meta">Stays until its file import is removed.</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        <div className="cmp-toolbar">
          <span className="cmp-meta">{from}–{to} of {total}</span>
          <div className="cmp-actions">
            <button type="button" className="cmp-btn" disabled={offset <= 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</button>
            <button type="button" className="cmp-btn" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>Next</button>
          </div>
        </div>
      </div>
      <form className="cmp-card cmp-stack" onSubmit={importCsv}>
        <h2>Import a file</h2>
        <p className="cmp-help">CSV or TXT. The default source is your company list.</p>
        <div className="cmp-row">
          <label className="cmp-field">
            Source
            <select className="cmp-select" value={importSource} onChange={(e) => setImportSource(e.target.value)}>
              {IMPORT_SOURCES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label className="cmp-field cmp-grow">
            File
            <input className="cmp-input" type="file" accept=".csv,.txt,text/csv,text/plain" onChange={(e) => setImportFile(e.target.files && e.target.files[0])} />
          </label>
          <button type="submit" className="cmp-btn cmp-btn--primary" disabled={busy}>Import</button>
        </div>
      </form>
    </div>
  );
}
